import { useId, useMemo, useRef, useState } from 'react';
import { useDropdownMenu } from '../../../hooks';
import {
  findOptionIndex,
  handleSelectTriggerKeyDown,
  moveHighlightIndex,
  normalizeOptions,
} from './index';

export function useSelectController({ value, options, onChange }) {
  const listboxId = useId();
  const buttonRef = useRef(null);
  const { rootRef, open, setOpen } = useDropdownMenu({ focusRef: buttonRef });

  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const selectedIndex = findOptionIndex(normalizedOptions, value);
  const selected = normalizedOptions[selectedIndex] ?? null;

  const [highlightedIndex, setHighlightedIndex] = useState(Math.max(0, selectedIndex));

  const commitChange = (nextValue) => {
    if (nextValue === value) {
      setOpen(false);
      return;
    }
    onChange?.(nextValue);
    setOpen(false);
  };

  const moveHighlight = (delta) =>
    setHighlightedIndex((prev) =>
      moveHighlightIndex({
        options: normalizedOptions,
        fromIndex: prev,
        delta,
      }),
    );

  const onTriggerKeyDown = (event) =>
    handleSelectTriggerKeyDown({
      event,
      open,
      selectedIndex,
      highlightedIndex,
      options: normalizedOptions,
      setOpen,
      setHighlightedIndex,
      moveHighlight,
      commitChange,
    });

  const onTriggerClick = () => {
    setOpen((v) => !v);
    setHighlightedIndex(Math.max(0, selectedIndex));
  };

  return {
    listboxId,
    rootRef,
    buttonRef,
    normalizedOptions,
    selectedLabel: selected?.label ?? '',
    open,
    highlightedIndex,
    setHighlightedIndex,
    commitChange,
    onTriggerKeyDown,
    onTriggerClick,
  };
}
